import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AnnouncementContent, { AnnouncementHero, hasWideAttachment, priorityTheme } from './AnnouncementContent';

const MAX_DOTS = 8;

// popup ประกาศทีละใบ — ขึ้นทุกครั้งที่เปิดเว็บ เว้นแต่ติ๊ก "ไม่ต้องแสดงอีก 1 วัน" ตอนปิด
// normal: ปิด = รับทราบ, important: ปิดไปก่อนได้หรือกดรับทราบ, critical: ต้องรับทราบก่อน (ครั้งแรก) ถึงปิดได้
export default function AnnouncementPopup({
  announcement, position, total, onClose,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [busy, setBusy] = useState(false);
  const [snooze, setSnooze] = useState(false);

  useEffect(() => {
    setSnooze(false);
  }, [announcement?.id]);

  if (!announcement) return null;

  const { priority } = announcement;
  const pt = priorityTheme(priority);
  const acknowledged = !!announcement.acknowledged_at;
  const mustAcknowledge = priority === 'critical' && !acknowledged;
  const showAckButton = priority !== 'normal' && !acknowledged;
  const primarySx = { px: 3.5, background: pt.gradient, boxShadow: `0 4px 12px ${alpha(pt.color, 0.35)}` };

  const close = async (acknowledge) => {
    setBusy(true);
    try {
      await onClose(announcement, { acknowledge, snooze });
    } finally {
      setBusy(false);
    }
  };

  // ปิดแบบปกติ (ปุ่มปิด / X / คลิกนอก / Esc) — normal นับว่ารับทราบไปด้วย
  const handleDismiss = () => {
    if (mustAcknowledge || busy) return;
    close(priority === 'normal' && !acknowledged);
  };

  return (
    <Dialog
      open
      onClose={handleDismiss}
      disableEscapeKeyDown={mustAcknowledge}
      maxWidth={hasWideAttachment(announcement) ? 'md' : 'sm'}
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 4,
          overflow: 'hidden',
          boxShadow: `0 24px 60px ${alpha(pt.color, 0.28)}`,
        },
      }}
    >
      <AnnouncementHero
        announcement={announcement}
        counter={total > 1 ? `${position} / ${total}` : null}
        onClose={mustAcknowledge ? null : handleDismiss}
      />

      {mustAcknowledge && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 3, py: 1, bgcolor: alpha('#ef4444', 0.08), color: '#b91c1c',
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={600}>ประกาศด่วน — กรุณาอ่านและกด “รับทราบ” ก่อนปิด</Typography>
        </Stack>
      )}

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <AnnouncementContent announcement={announcement} />
      </DialogContent>

      <Box sx={{
        px: 3, py: 1.75, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#f9fafb',
      }}
      >
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5}>
          <FormControlLabel
            sx={{ mr: 'auto', ml: -0.5 }}
            control={(
              <Checkbox
                size="small"
                checked={snooze}
                onChange={(e) => setSnooze(e.target.checked)}
              />
            )}
            label={<Typography variant="body2" color="text.secondary">ไม่ต้องแสดงอีก 1 วัน</Typography>}
          />
          {total > 1 && total <= MAX_DOTS && (
            <Stack direction="row" spacing={0.6} justifyContent="center" sx={{ px: 1 }}>
              {Array.from({ length: total }, (_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: i + 1 === position ? 18 : 7,
                    height: 7,
                    borderRadius: 4,
                    bgcolor: i + 1 === position ? pt.color : alpha(pt.color, 0.25),
                    transition: 'width 0.2s',
                  }}
                />
              ))}
            </Stack>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {!mustAcknowledge && (
              <Button
                onClick={handleDismiss}
                disabled={busy}
                variant={showAckButton ? 'text' : 'contained'}
                color={showAckButton ? 'inherit' : 'primary'}
                sx={showAckButton ? {} : primarySx}
              >
                {total > 1 && position < total ? 'ถัดไป' : 'ปิด'}
              </Button>
            )}
            {showAckButton && (
              <Button
                variant="contained"
                onClick={() => close(true)}
                disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                sx={primarySx}
              >
                รับทราบ
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}
